import { normalizeTimezoneValue, getBrowserTimezone } from '@/utils/timezones'
import actions from './actions'; //


const state = () => ({
  creatorCalendarId: '',
  selectedClinicianCalendarIds: [],
  // Default to the browser's timezone, not a hardcoded "UTC" — until the authoritative
  // REST setting loads, this keeps views/formatting on the user's actual zone instead of
  // silently rendering everything in UTC.
  calendarTimezone: getBrowserTimezone(),
  calendarLoading: false,
  taskLoading: false,
  currentEventInfo: null,
  currentTaskInfo: null,
  createOrUpdateEventAction: false,
  preScheduleInfo: {},
  lastCreatedEventResponse: null,
  lastCreatedTaskResponse: null,
  lastUpdatedTaskResponse: null,
  lastUpdatedEventResponse: null,
  // Set when a create/update submit rejects, so the dialog can tell a finished submit
  // apart from a failed one (the createOrUpdateEventAction flag resets for both).
  lastSubmitFailed: false,

  // raw data
  rawEventList: [],
  rawTaskInfoList: [],
  rawMeetingRequestList: [],
  canceledMettingRequestList: [],
  rawProviderList: [],
  rawPatientList: [],
  rawLocationList: [],
  nextAvailableTimeList: [],
  //setting
  userSettings: null,
  rawCustomerSchedule: [],
  rawEventTypeList: [],
  // Zoom integration
  isZoomAuthenticated: false,
  
  // Cached formatted lists (computed once, stored in state)
  _cachedFormattedPatientsList: null,
  _cachedFormattedProvidersList: null,
});
  
const mutations = {
  SET_CURRENT_EVENT_INFO(state, eventInfo) {
    state.currentEventInfo = eventInfo;
  },
  SET_CURRENT_TASK_INFO(state, taskInfo) {
    state.currentTaskInfo = taskInfo;
  },

  SET_CREATE_OR_UPDATE_EVENT_ACTION(state, action) {
    state.createOrUpdateEventAction = action;
  },
  SET_LAST_SUBMIT_FAILED(state, failed) {
    state.lastSubmitFailed = Boolean(failed);
  },
  SET_CREATOR_CALENDAR_ID(state, calendarId) {
    state.creatorCalendarId = calendarId;
  },
  SET_SELECTED_CLINICIAN_CALENDAR_IDS(state, calendarIds) {
    state.selectedClinicianCalendarIds = Array.isArray(calendarIds) ? [...calendarIds] : [];
  },
  SET_CALENDAR_TIMEZONE(state, timezone) {
    state.calendarTimezone = normalizeTimezoneValue(timezone || 'UTC');
  },
  SET_CALENDAR_LOADING(state, loading) {
    state.calendarLoading = loading;
  },
  SET_TASK_LOADING(state, loading) {
    state.taskLoading = loading;
  },
  SET_RAW_EVENT_LIST(state, eventList) {
    state.rawEventList = [...eventList];
  },
  SET_RAW_TASK_INFO_LIST(state, taskInfoList) {
    state.rawTaskInfoList = [...taskInfoList];
  },
  SET_RAW_MEETING_REQUEST_LIST(state, meetingRequestList) {
    state.rawMeetingRequestList = meetingRequestList;
  },
  SET_RAW_PROVIDER_LIST(state, providerList) {
    state.rawProviderList = providerList;
  },
  SET_RAW_PATIENT_LIST(state, patientList) {
    state.rawPatientList = patientList;
  },
  UPDATE_RAW_PATIENT_CALENDAR_ID(state, { patientId, calendarId }) {
    if (!state.rawPatientList) return;
    state.rawPatientList = state.rawPatientList.map(p =>
      p.id === patientId ? { ...p, calendar_id: calendarId } : p
    );
  },
  SET_RAW_LOCATION_LIST(state, locationList) {
    state.rawLocationList = locationList;
  },
  SET_CANCELED_MEETING_REQUEST_LIST(state, canceledMettingRequestList) {
    state.canceledMettingRequestList = canceledMettingRequestList;
  },
  SET_NEXT_AVAILABLE_TIME(state, nextAvailableTimeList) {
    state.nextAvailableTimeList = nextAvailableTimeList;
  },
  SET_USER_SETTINGS(state, userSettings) {
    state.userSettings = userSettings;
  },
  SET_RAW_CUSTOMER_SCHEDULE(state, customerSchedule) {
    state.rawCustomerSchedule = customerSchedule;
  },
  SET_RAW_EVENT_TYPE_LIST(state, eventTypeList) {
    state.rawEventTypeList = eventTypeList;
  },
  SET_PRE_SCHEDULE_INFO(state, preScheduleInfo) {
    state.preScheduleInfo = preScheduleInfo;
  },
  SET_LAST_CREATED_EVENT_RESPONSE(state, response) {
    state.lastCreatedEventResponse = response;
  },
  SET_LAST_CREATED_TASK_RESPONSE(state, response) {
    state.lastCreatedTaskResponse = response;
  },
  SET_LAST_UPDATED_TASK_RESPONSE(state, response) {
    state.lastUpdatedTaskResponse = response;
  },
  SET_ZOOM_AUTHENTICATED(state, isAuthenticated) {
    state.isZoomAuthenticated = isAuthenticated;
  },
  SET_LAST_UPDATED_EVENT_RESPONSE(state, response) {
    state.lastUpdatedEventResponse = response;
  },
};
  
const getters = {
  creatorCalendarId: (s) => s.creatorCalendarId,
  selectedClinicianCalendarIds: (s) => s.selectedClinicianCalendarIds,
  calendarTimezone: (s) => s.calendarTimezone,
  calendarLoading: (s) => s.calendarLoading,
  createOrUpdateEventAction: (s) => s.createOrUpdateEventAction,
  currentEventInfo: (s) => s.currentEventInfo,
  currentTaskInfo: (s) => s.currentTaskInfo,
  preScheduleInfo: (s) => s.preScheduleInfo,
  lastUpdatedEventResponse: (s) => s.lastUpdatedEventResponse,

  rawTaskInfoList: (s) => s.rawTaskInfoList,
  rawMeetingRequestList: (s) => s.rawMeetingRequestList,
  canceledMettingRequestList: (s) => s.canceledMettingRequestList,
  rawProviderList: (s) => s.rawProviderList,
  rawPatientList: (s) => s.rawPatientList,
  rawLocationList: (s) => s.rawLocationList,
  nextAvailableTimeList: (s) => s.nextAvailableTimeList,
  //setting
  userSettings: (s) => s.userSettings,
  rawCustomerSchedule: (s) => s.rawCustomerSchedule,
  rawEventTypeList: (s) => s.rawEventTypeList,
  // Zoom
  isZoomAuthenticated: (s) => s.isZoomAuthenticated,
  
  // Cached formatted lists to avoid recomputing on every component render
  formattedPatientsList: (s) => {
    if (!s.rawPatientList || s.rawPatientList.length === 0) return []
    return s.rawPatientList.map(({ id, first_name, last_name, email, calendar_id }) => ({
      id: id,
      name: `${first_name} ${last_name}`,
      email: email || '',
      calendar_id: calendar_id,
    }))
  },
  formattedProvidersList: (s) => {
    if (!s.rawProviderList || s.rawProviderList.length === 0) return []
    return s.rawProviderList
      .filter(({ first_name, last_name }) => first_name || last_name)
      .map(({ customer_id, first_name, last_name, calendar_id }) => ({
      id: customer_id,
      name: `${first_name || ''} ${last_name || ''}`.trim(),
      calendar_id: calendar_id,
    }))
  },
};
  
export default {
  namespaced: true,
  state,  
  mutations,
  actions,
  getters,
};
  